import { DateTimePicker } from '@liza/ui/components/atoms/DateTimePicker';
import { getUnansweredRequiredLinkIds } from '@liza/ui/components/records';
import { ValueSetCombobox, type ValueSetCode } from '@liza/ui/components/records/ValueSetCombobox';
import { Button } from '@liza/ui/components/ui/button';
import { Field, FieldGroup, FieldLabel } from '@liza/ui/components/ui/field';
import { Spinner } from '@liza/ui/components/ui/spinner';
import { Textarea } from '@liza/ui/components/ui/textarea';
import type { UseValueSetExpandResult } from '@liza/ui/hooks/useValueSetExpand';
import type { Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { useForm } from '@tanstack/react-form';
import { XIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Chip } from './Chip';
import { SelectedTestRow } from './SelectedTestRow';

export type { ValueSetCode };

/**
 * `ServiceRequest.priority` codes Health Gorilla accepts. Mirrors `PRIORITY_VALUES` in
 * `labOrder.ts` (backend) — keep the two in sync.
 */
export type LabOrderPriority = 'routine' | 'urgent' | 'asap' | 'stat';

/**
 * Performing lab as returned by `searchPerformingLabs`. `medplumId` is the Medplum resource id;
 * `hgId` is the Health Gorilla–side organization identifier and is what HG's test catalog endpoint
 * expects when searching the catalog for that lab.
 */
export interface PerformingLabOption {
  medplumId: string;
  hgId: string;
  name?: string;
}

/** Lab test `Coding` returned by the HG autocomplete. */
export interface TestOption {
  system?: string;
  code: string;
  display?: string;
}

/** A selected test plus its optional per-test priority override and freeform note. */
export interface SelectedTest extends TestOption {
  priority?: LabOrderPriority;
  notes?: string;
}

/** Items + autocomplete handlers for an async-search field. */
export interface AsyncSearchProps<T> {
  items: T[];
  onQueryChange: (query: string) => void;
  isFetching: boolean;
  error?: string;
}

/**
 * AOE data captured per test, keyed by `testKey`. Kept in component state rather than the form
 * value: the deeply-recursive FHIR `Questionnaire`/`QuestionnaireResponse` types blow TanStack
 * Form's type instantiation (TS2589) if embedded in the form value.
 */
export interface TestAoe {
  /** True once the test's AOE questionnaire fetch has settled (questionnaire known, or confirmed
   *  the test has none). Submit waits for this so questions can't be skipped mid-download. */
  loaded?: boolean;
  questionnaire?: Questionnaire;
  responses?: QuestionnaireResponse;
}

const formSchema = z.object({
  performingLab: z
    .custom<PerformingLabOption | undefined>()
    .refine((v): v is PerformingLabOption => v !== undefined, { message: 'Performing lab is required' }),
  selectedTests: z
    .custom<SelectedTest[]>()
    .refine((v) => Array.isArray(v) && v.length > 0, { message: 'At least one test must be selected' }),
  diagnoses: z
    .custom<ValueSetCode[]>()
    .refine((v) => Array.isArray(v) && v.length > 0, { message: 'At least one diagnosis is required' }),
  orderNotes: z.string(),
  specimenCollectedDateTime: z.string(),
});

export type AddLabOrderFormValues = z.infer<typeof formSchema>;

/**
 * Internal mutable form state. Wider than the validated {@link AddLabOrderFormValues}: required
 * fields may be empty mid-edit (no performing lab yet, no tests selected). The submit validator
 * narrows to `AddLabOrderFormValues` before `onCreate` runs.
 */
export interface AddLabOrderFormState {
  performingLab: PerformingLabOption | undefined;
  selectedTests: SelectedTest[];
  diagnoses: ValueSetCode[];
  orderNotes: string;
  specimenCollectedDateTime: string;
}

const EMPTY_FORM_VALUES: AddLabOrderFormState = {
  performingLab: undefined,
  selectedTests: [],
  diagnoses: [],
  orderNotes: '',
  specimenCollectedDateTime: '',
};

/** What the form hands to `onCreate`: the form values plus each test's captured AOE answers. */
export interface AddLabOrderSubmission extends Omit<AddLabOrderFormValues, 'selectedTests'> {
  selectedTests: (SelectedTest & { aoeResponses?: QuestionnaireResponse })[];
}

export interface AddLabOrderFormProps {
  /** Async-search handlers for the performing lab combobox. */
  performingLab: AsyncSearchProps<PerformingLabOption>;
  /**
   * Async-search handlers for the test catalog. The catalog is per-lab, so the parent gates
   * fetches on the currently-selected performing lab; this component just renders whatever
   * `items` it's given.
   */
  tests: AsyncSearchProps<TestOption>;
  /** Async-search handlers for ICD-10 diagnoses (driven by `useValueSetExpand`). */
  diagnoses: UseValueSetExpandResult;
  onCreate: (values: AddLabOrderSubmission) => Promise<void>;
  onClose: () => void;
  isCreating: boolean;
  /** Notifies the parent when the performing lab selection changes (so it can re-scope the test catalog). */
  onPerformingLabChange: (lab: PerformingLabOption | null) => void;
  /**
   * `footer` (default) renders Submit/Cancel below the fields — for the modal dialog. `drawer`
   * renders a Save/close header on top (matching the encounter composer) and scrolls the fields.
   */
  layout?: 'footer' | 'drawer';
  /** Header title shown in `drawer` layout. */
  title?: string;
  /** Seeds the initial form state. Used for prefill (e.g. reordering) and Storybook stories. */
  defaultValues?: Partial<AddLabOrderFormState>;
}

const labLabel = (lab: PerformingLabOption): string => lab.name ?? lab.medplumId;
const testKey = (test: TestOption): string => `${test.system ?? ''}|${test.code}`;
const testLabel = (test: TestOption): string => test.display ?? test.code;
const diagnosisKey = (diagnosis: ValueSetCode): string => `${diagnosis.system ?? ''}|${diagnosis.code}`;
const diagnosisLabel = (diagnosis: ValueSetCode): string => diagnosis.display ?? diagnosis.code;
const sameTest = (a: TestOption, b: TestOption): boolean => a.code === b.code && a.system === b.system;

export function AddLabOrderForm({
  performingLab,
  tests,
  diagnoses,
  onCreate,
  onClose,
  isCreating,
  onPerformingLabChange,
  layout = 'footer',
  title,
  defaultValues,
}: AddLabOrderFormProps): React.ReactNode {
  const { t } = useTranslation('records');
  const { t: tCommon } = useTranslation('common');

  // AOE answers live outside the form value (see TestAoe), keyed by testKey.
  const [aoeByTest, setAoeByTest] = useState<Record<string, TestAoe>>({});

  // Block submit until each selected test's AOE is ready and any required questions are answered:
  // a test that's still downloading or errored isn't `loaded`, and a loaded test with required
  // questions must have them answered. Tests with no AOEs (loaded, no questionnaire) don't block.
  const aoeBlocksSubmit = (selectedTests: SelectedTest[]): boolean =>
    selectedTests.some((test) => {
      const aoe = aoeByTest[testKey(test)];
      if (!aoe?.loaded) return true;
      if (!aoe.questionnaire) return false;
      return getUnansweredRequiredLinkIds(aoe.questionnaire, aoe.responses).length > 0;
    });

  const form = useForm({
    // Spreading the optional overrides onto the full base guarantees the complete state shape; the
    // assertion just restores the precise field types the spread of a `Partial` widens.
    defaultValues: { ...EMPTY_FORM_VALUES, ...defaultValues } as AddLabOrderFormState,
    validators: { onChange: formSchema, onMount: formSchema, onSubmit: formSchema },
    onSubmit: async ({ value }) => {
      // TanStack Form types `value` from `defaultValues` (`performingLab: undefined`), so it's
      // wider than `AddLabOrderFormValues`. The runtime validator guarantees the schema shape by
      // the time we reach this callback; `formSchema.parse` narrows the type.
      const parsed = formSchema.parse(value);
      await onCreate({
        ...parsed,
        selectedTests: parsed.selectedTests.map((test) => ({
          ...test,
          aoeResponses: aoeByTest[testKey(test)]?.responses,
        })),
      });
    },
  });

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    void form.handleSubmit();
  };

  const isDrawer = layout === 'drawer';

  const submitButton = (
    <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting, state.values.selectedTests] as const}>
      {([canSubmit, isSubmitting, selectedTests]) => (
        <Button
          type="submit"
          size="sm"
          disabled={!canSubmit || isSubmitting || isCreating || aoeBlocksSubmit(selectedTests)}
          className={isDrawer ? undefined : 'flex-1'}
          aria-label={t('submitLabOrder')}
          data-testid="lab-order-submit-button"
        >
          {isSubmitting || isCreating ? <Spinner className="size-4" /> : isDrawer ? t('save') : t('submitLabOrder')}
        </Button>
      )}
    </form.Subscribe>
  );

  const fields = (
    <FieldGroup>
      <form.Field name="performingLab">
        {(field) => (
          <ValueSetCombobox
            id={field.name}
            label={t('performingLab')}
            placeholder={t('performingLabPlaceholder')}
            items={performingLab.items.map((lab) => ({
              system: 'Organization',
              code: lab.medplumId,
              display: labLabel(lab),
            }))}
            value={
              field.state.value
                ? {
                    system: 'Organization',
                    code: field.state.value.medplumId,
                    display: labLabel(field.state.value),
                  }
                : null
            }
            onValueChange={(value) => {
              if (value === null) {
                field.handleChange(undefined);
                onPerformingLabChange(null);
                // Clear selected tests — the catalog is per-lab so prior picks are no longer valid.
                form.setFieldValue('selectedTests', []);
                setAoeByTest({});
                return;
              }
              const lab = performingLab.items.find((l) => l.medplumId === value.code);
              if (!lab) return;
              field.handleChange(lab);
              onPerformingLabChange(lab);
              form.setFieldValue('selectedTests', []);
              setAoeByTest({});
            }}
            onQueryChange={performingLab.onQueryChange}
            isLoading={performingLab.isFetching}
            error={performingLab.error}
            emptyMessage={t('noMatchingResults')}
            required
          />
        )}
      </form.Field>

      <form.Field name="selectedTests">
        {(field) => {
          const selected = field.state.value;
          const labSelected = !!form.state.values.performingLab;
          return (
            <>
              <ValueSetCombobox
                id="lab-order-tests-add"
                label={t('selectedTests')}
                placeholder={labSelected ? t('selectedTestsPlaceholder') : t('selectPerformingLabFirst')}
                items={tests.items
                  .filter((test) => !selected.some((s) => sameTest(s, test)))
                  .map((test) => ({ system: test.system, code: test.code, display: testLabel(test) }))}
                value={null}
                onValueChange={(value) => {
                  if (!value) return;
                  const test = tests.items.find((tst) => tst.code === value.code && tst.system === value.system);
                  if (!test) return;
                  field.handleChange([...selected, test]);
                }}
                onQueryChange={tests.onQueryChange}
                isLoading={tests.isFetching}
                error={tests.error}
                emptyMessage={t('noMatchingResults')}
                required
                disabled={!labSelected}
              />
              {selected.length > 0 && (
                <form.Subscribe selector={(state) => state.submissionAttempts}>
                  {(submissionAttempts) => (
                    <div className="flex flex-col gap-4 pt-2">
                      {selected.map((test) => (
                        <SelectedTestRow
                          key={testKey(test)}
                          test={test}
                          name={testLabel(test)}
                          onChange={(next) => field.handleChange(selected.map((s) => (sameTest(s, test) ? next : s)))}
                          onRemove={() => field.handleChange(selected.filter((s) => !sameTest(s, test)))}
                          aoe={aoeByTest[testKey(test)]}
                          onAoeChange={(entry) => setAoeByTest((prev) => ({ ...prev, [testKey(test)]: entry }))}
                          showAoeErrors={submissionAttempts > 0}
                        />
                      ))}
                    </div>
                  )}
                </form.Subscribe>
              )}
            </>
          );
        }}
      </form.Field>

      <form.Field name="diagnoses">
        {(field) => {
          const selected = field.state.value;
          return (
            <>
              <ValueSetCombobox
                id="lab-order-diagnoses-add"
                label={t('diagnoses')}
                placeholder={t('diagnosesPlaceholder')}
                items={diagnoses.items.filter((d) => !selected.some((s) => s.code === d.code && s.system === d.system))}
                value={null}
                onValueChange={(value) => {
                  if (!value) return;
                  field.handleChange([...selected, value]);
                }}
                onQueryChange={diagnoses.onQueryChange}
                isLoading={diagnoses.isFetching}
                error={diagnoses.error}
                emptyMessage={t('noMatchingResults')}
                required
              />
              {selected.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-2">
                  {selected.map((diagnosis) => (
                    <Chip
                      key={diagnosisKey(diagnosis)}
                      label={`${diagnosis.code} — ${diagnosisLabel(diagnosis)}`}
                      onRemove={(): void =>
                        field.handleChange(
                          selected.filter((s) => !(s.code === diagnosis.code && s.system === diagnosis.system)),
                        )
                      }
                      removeAriaLabel={t('removeDiagnosis', { name: diagnosisLabel(diagnosis) })}
                    />
                  ))}
                </div>
              )}
            </>
          );
        }}
      </form.Field>

      {/* Billing is patient-only in v1, so it's shown as static text. The order is submitted with
          the backend's default `billTo: 'patient'`; insurance billing isn't surfaced here. */}
      <Field>
        <p className="text-sm" data-testid="lab-order-bill-to">
          <span className="font-medium">{`${t('billTo')}:`}</span> {t('billTo_patient')}
        </p>
      </Field>

      <form.Field name="specimenCollectedDateTime">
        {(field) => (
          <Field>
            <FieldLabel htmlFor={field.name}>{t('specimenCollectionTime')}</FieldLabel>
            <DateTimePicker
              id={field.name}
              value={field.state.value}
              onChange={field.handleChange}
              onBlur={field.handleBlur}
              aria-label={t('specimenCollectionTime')}
              data-testid="lab-order-specimen-datetime"
            />
          </Field>
        )}
      </form.Field>

      <form.Field name="orderNotes">
        {(field) => (
          <Field>
            <FieldLabel htmlFor={field.name}>{t('orderNotes')}</FieldLabel>
            <Textarea
              id={field.name}
              name={field.name}
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              aria-label={t('orderNotes')}
              rows={3}
            />
          </Field>
        )}
      </form.Field>
    </FieldGroup>
  );

  if (isDrawer) {
    return (
      <form onSubmit={handleSubmit} className="flex h-full flex-col bg-card/50">
        <div className="flex shrink-0 items-center gap-2 border-b px-4 py-2">
          <h2 className="text-sm font-semibold">{title}</h2>
          <div className="flex-1" />
          {submitButton}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            onClick={onClose}
            disabled={isCreating}
            aria-label={tCommon('close')}
          >
            <XIcon className="size-4" />
          </Button>
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">{fields}</div>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {fields}
      <div className="flex gap-2">
        {submitButton}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onClose}
          disabled={isCreating}
          className="flex-1"
          aria-label={t('cancelLabOrder')}
        >
          {t('cancel')}
        </Button>
      </div>
    </form>
  );
}
